import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled, { css, keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faClipboard,
  faComments,
} from "@fortawesome/free-solid-svg-icons";
import { Row, Col, Card, Button, Modal } from "react-bootstrap";
import { fetchAllReservations } from "../actions/restaurantActions";

import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import CollapsibleChat from "./chat/CollapsibleChat";
import ReservationCard from "./ReservationCard";

const slideIn = keyframes`
  from {
    transform: translateX(100%); /* Start from the right */
  }
  to {
    transform: translateX(0); /* Slide to original position */
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0); /* Start from original position */
  }
  to {
    transform: translateX(100%); /* Slide to the right */
  }
`;

const BarContainer = styled.div`
  width: 100vw; /* Viewport width */
  height: 100%; /* Viewport height */
  position: fixed;
  top: 0; /* Position from the top */
  left: 0; /* Position from the left */
  z-index: 1000; /* High z-index to stay on top */
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.2);

  /* Conditional animations */
  animation: ${(props) => (props.clicked ? slideIn : slideOut)} 0.5s forwards;
`;

const InsideContainerBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ee754c;
  overflow: hidden;
  display: flex;
  justify-content: end;
`;

const InsideContainer = styled.div`
  padding: 30px 5px 5px 10px;
  position: relative;
  width: 98%;
  background-color: white;
  height: 100%;
  overflow: hidden;

  /* Estilização da scrollbar */
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #e20202ff;
    &:hover {
      background-color: #bbbbbb;
    }
  }

  &::-webkit-scrollbar {
    width: 12px;
    background-color: transparent;
  }

  /* Media query para ecrãs pequenos */
  @media (max-width: 768px) {
    padding-top: 40px; /* Aumenta 10px no padding-top */
    padding-top: 10px; /* Ou usa margin-top para descer o container */

    /* Opcional: ajustar outros elementos para mobile */
    padding-left: 15px;
    padding-right: 15px;
  }

  /* Media query para ecrãs muito pequenos */
  @media (max-width: 480px) {
    padding-top: 50px; /* Pode ajustar ainda mais se necessário */
  }
`;

const orbit = (start, end) => keyframes`
  from {
    transform: rotate(${start}deg);
  }
  to {
    transform: rotate(${end}deg);
  }
`;

const CircleContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: fixed;
  top: 15px;
  right: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #98afad;
  cursor: pointer;
  padding: 2px;
  z-index: 1001;

  @media (max-width: 768px) {
    position: fixed;
    top: 10px;
    right: 50px;
  }
`;

const NotificationCircle = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;

  bottom: 20px; /* Half the size of the small circle */
  right: 20px; /* Half the size of the small circle */

  /* This wrapper helps keep the number inside the circle straight while the circle itself rotates. */
  & > div {
    display: flex;
    justify-content: center;
    align-items: center; /* Counter the initial 45deg rotation of the parent */
  }

  ${({ animationDegrees, animate }) =>
    animate &&
    css`
      transform-origin: 29px 27px; /* The center of the main circle */
      animation: ${orbit(animationDegrees.start, animationDegrees.end)} 0.5s
        forwards;
    `}
`;

const OrangeNotification = styled(NotificationCircle)`
  color: white;
  background: #ee754c;
`;

const RedCircle = styled(NotificationCircle)`
  color: white;
  background: red;
`;

const tremble = keyframes`
0% { transform: rotate(0deg); }
25% { transform: rotate(-6deg); }
75% { transform: rotate(6deg); }
100% { transform: rotate(0deg); }
`;

const AnimatedIcon = styled(FontAwesomeIcon)`
  color: #5fddb3;
  font-size: 60px;
  ${({ animate }) =>
    animate &&
    css`
      animation: ${tremble} 0.2s ease-in-out 3; /* Adjust time and iteration count as needed */
    `}
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 10px;
  height: 10px;
  background-color: #ee754c;
  border-radius: 50%;
  border: 1px solid white;
`;

function NotificationBell({
  restaurant_id,
  allReservations,
  reservationsWithChatMessages,
  reservations_past_estimated_time,
}) {
  const [activeKey, setActiveKey] = useState("reservas");

  const userInfoState = useSelector((state) => state.userLogin);
  const { userInfo, isLoading } = userInfoState;

  const manager_info = useSelector((state) => state.crudManager);
  const { managerInfo } = manager_info;

  const modalRef = useRef(null);
  const modalPhoneRef = useRef(null);
  const modalCancelRef = useRef(null);
  const notificationBellRef = useRef(null);
  const editModalRef = useRef(null);
  const statusBoxRef = useRef(null);
  const colRef = useRef(null); // Step 1: Create a reference

  const dispatch = useDispatch();
  const [clicked, setClicked] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [pendingNumber, setPendingNumber] = useState(0);
  const [cancelledNumber, setCancelledNumber] = useState(0);

  const [
    allReservationsWithDateSeparators,
    setAllReservationsWithDateSeparators,
  ] = useState([]);

  const [reset, setReset] = useState(false);

  const contentRef = useRef(null);

  const [hasUnreadChatNotifications, setHasUnreadChatNotifications] =
    useState(false);

  const [hasNewReservation, setHasNewReservation] = useState(false);

  useEffect(() => {
    if (allReservations) {
      console.log(" pew pew: ", allReservations);
      // Verifica se há mensagens não lidas no chat
      const hasUnread = allReservations.some((reservation) => {
        const notifications =
          typeof reservation.chat_notification_restaurant === "string"
            ? reservation.chat_notification_restaurant.split(",")
            : [];
        return (
          (reservation.chat_notification_restaurant_all ||
            (reservation.chat_notification_restaurant &&
              notifications.includes(userInfo.name))) &&
          reservation.chat_has_messages
        );
      });
      setHasUnreadChatNotifications(hasUnread);

      const hasNew = allReservations.some(
        (reservation) =>
          reservation.status === "Pending_from_restaurant" ||
          reservation.status === "Edited" ||
          reservation.status === "Waiting_List" ||
          reservation.cancelled_warning === true,
      );
      setHasNewReservation(hasNew);
    }
  }, [allReservations, userInfo]);

  // Scroll to top handler
  const handleScrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Modified to scroll up when tab changes
  const handleTabSelect = (key) => {
    setActiveKey(key);
    handleScrollToTop();
  };

  const handleChatToggle = () => {};

  const stableRestaurantId = useMemo(() => restaurant_id, [restaurant_id]);

  useEffect(() => {
    if (stableRestaurantId) {
      dispatch(
        fetchAllReservations(
          stableRestaurantId,
          null,
          null,
          null,
          "all_pending_reservations_from_all_time",
        ),
      );
    }
  }, [dispatch, stableRestaurantId]);

  const PENDING_STATUSES = new Set([
    "Pending_from_restaurant",
    "Edited",
    "Waiting_List",
  ]);

  // ✅ states you need
  const [freeTablesReservations, setFreeTablesReservations] = useState([]);
  const [pendingReservations, setPendingReservations] = useState([]);

  useEffect(() => {
    setFreeTablesReservations(
      reservations_past_estimated_time?.filter(
        (r) => String(r.status).toLowerCase() === "attended",
      ) ?? [],
    );

    setPendingReservations(
      allReservations?.filter(
        (r) =>
          PENDING_STATUSES.has(r.status) ||
          !!r.cancelled_warning ||
          !!r.chat_notification_restaurant_all,
      ) ?? [],
    );
  }, [reservations_past_estimated_time, allReservations]);

  useEffect(() => {
    if (!allReservations?.length) {
      // still count free tables even if there are no reservations
      const freeTablesCount =
        reservations_past_estimated_time?.filter(
          (r) => String(r.status).toLowerCase() === "attended",
        ).length ?? 0;

      setPendingNumber(freeTablesCount);
      return;
    }

    const pendingBaseCount = allReservations.filter(
      (r) =>
        r.cancelled_warning === true ||
        r.status === "Cancelled_By_Customer_Warning" ||
        r.status === "Pending_from_restaurant" ||
        r.status === "new_chat_message" ||
        r.status === "Edited" ||
        r.chat_notification_restaurant_all,
    ).length;

    const freeTablesCount =
      reservations_past_estimated_time?.filter(
        (r) => String(r.status).toLowerCase() === "attended",
      ).length ?? 0;

    setPendingNumber(pendingBaseCount + freeTablesCount);
  }, [allReservations, reservations_past_estimated_time]);

  useEffect(() => {
    if (allReservations && allReservations.length > 0) {
      // Make sure your dates are sorted, if they're not already.
      // This sort function assumes your dates are in 'YYYY-MM-DD' format and are strings.
      const sortedReservations = [...allReservations].sort((a, b) =>
        a.date.localeCompare(b.date),
      );

      const reservationsWithSeparators = [];
      let lastDate = null;

      sortedReservations.forEach((reservation) => {
        // If this reservation is on a different day, add a date separator.
        if (reservation.date !== lastDate) {
          reservationsWithSeparators.push({
            isDateSeparator: true,
            date: reservation.date,
          });
          lastDate = reservation.date;
        }
        // Add the regular reservation.
        reservationsWithSeparators.push(reservation);
      });

      setAllReservationsWithDateSeparators(reservationsWithSeparators);
    }
  }, [allReservations]);

  // Define the start and end points for the animation
  const animationDegreesYellow = clicked
    ? { start: 180, end: 0 } // Clicked: Go to the other side
    : { start: 0, end: 180 }; // Unclicked: Return to the original position
  const animationDegreesRed = clicked
    ? { start: 140, end: -40 } // Clicked: Go to the other side
    : { start: -40, end: 140 }; // Unclicked: Return to the original position

  const counterRotate = clicked ? "rotate(0deg)" : "rotate(-180deg)";
  const counterRotateRed = clicked ? "rotate(40deg)" : "rotate(-140deg)";

  // New function to handle click
  const handleClick = () => {
    setAnimate(true);
    if (!clicked) {
      setClicked(true);
    } else {
      setClicked(false);
    }
    // Timeout duration should match your animation duration
    setTimeout(() => {
      setAnimate(false);
    }, 200); // this should match the duration of your tremble animation
  };

  const handleClickOutside = (event) => {
    // Check if the clicked target is the modal, notification bell, or their descendants
    if (
      (notificationBellRef.current &&
        notificationBellRef.current.contains(event.target)) ||
      (editModalRef.current && editModalRef.current.contains(event.target)) ||
      (modalCancelRef.current &&
        modalCancelRef.current.contains(event.target)) ||
      (modalPhoneRef.current && modalPhoneRef.current.contains(event.target)) ||
      (statusBoxRef.current && statusBoxRef.current.contains(event.target)) ||
      (modalRef.current && modalRef.current.contains(event.target))
    ) {
      // Clicked inside the modal or on the notification bell, ignore the click
      return;
    }
    if (colRef.current && !colRef.current.contains(event.target)) {
      setClicked(false);
    }
  };

  useEffect(() => {
    // Step 3: Add event listener when component mounts
    document.addEventListener("mousedown", handleClickOutside);

    // Remove event listener on cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <CircleContainer ref={notificationBellRef} onClick={handleClick}>
        <AnimatedIcon
          animate={animate}
          icon={faBell}
          style={{ color: "#ffffff", fontSize: "30px" }}
        />
        {pendingNumber > 0 && (
          <OrangeNotification
            animate={true}
            animationDegrees={animationDegreesYellow}
          >
            <div style={{ transform: counterRotate }}>
              {pendingNumber < 10 ? pendingNumber : "9+"}
            </div>{" "}
            {/* Wrapping the number to keep it straight */}
          </OrangeNotification>
        )}
        {cancelledNumber > 0 && (
          <RedCircle animate={true} animationDegrees={animationDegreesRed}>
            <div style={{ transform: counterRotateRed }}>
              {cancelledNumber < 10 ? cancelledNumber : "9+"}
            </div>{" "}
            {/* Wrapping the number to keep it straight */}
          </RedCircle>
        )}
      </CircleContainer>
      <BarContainer clicked={clicked}>
        <Row
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "end",
            padding: 0,
            margin: "0",
          }}
        >
          <Col
            ref={colRef}
            xs={12}
            sm={12}
            md={6}
            xl={4}
            style={{
              color: "black",
              height: "100%",
              padding: 0,
              margin: "0",
            }}
          >
            {}
            <InsideContainerBox>
              {/* HERE START OUR SUPER NICE SLIDE BAR */}
              <div className="menu-left"></div>
              <InsideContainer>
                <div style={{ position: "relative", height: "100%" }}>
                  <Tabs
                    activeKey={activeKey}
                    onSelect={handleTabSelect}
                    defaultActiveKey="reservas"
                    id="uncontrolled-tab-example"
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "white",
                      zIndex: 1000,
                    }}
                  >
                    <Tab
                      eventKey="reservas"
                      title={
                        <>
                          <b style={{ color: "inherit" }}>Reservas</b>
                          <FontAwesomeIcon
                            icon={faClipboard}
                            style={{
                              marginLeft: "5px",
                              color: "inherit",
                            }}
                          />
                          {hasNewReservation && <NotificationDot />}
                        </>
                      }
                    />
                    <Tab
                      eventKey="mensagens"
                      title={
                        <>
                          <b style={{ color: "inherit" }}>Mensagens</b>
                          <FontAwesomeIcon
                            icon={faComments}
                            style={{ marginLeft: "5px", color: "inherit" }}
                          />
                          {hasUnreadChatNotifications && <NotificationDot />}
                        </>
                      }
                    />
                  </Tabs>

                  <div
                    ref={contentRef}
                    style={{
                      position: "absolute",
                      top: "65px", // Start content 100px from top
                      bottom: 0,
                      left: 0,
                      right: 0,
                      width: "100%",
                      overflowY: "auto",
                      overflowBehaviour: "contain",
                    }}
                  >
                    {activeKey === "reservas" ? (
                      <div
                        style={{
                          overflowBehaviour: "contain",
                          width: "100%",
                        }}
                      >
                        {pendingReservations.length > 0 && (
                          <>
                            <div
                              style={{
                                borderBottom: "1px solid #99b0ad",
                                marginBottom: "10px",
                                color: "#99b0ad",
                              }}
                            >
                              Reservas por aceitar
                            </div>

                            <ReservationCard
                              style={{ width: "100%" }}
                              reservations={pendingReservations}
                              isNotifications={true}
                            />
                          </>
                        )}

                        {freeTablesReservations.length > 0 && (
                          <>
                            <div
                              style={{
                                borderBottom: "1px solid #99b0ad",
                                marginBottom: "10px",
                                color: "#99b0ad",
                              }}
                            >
                              Libertar mesas
                            </div>

                            <ReservationCard
                              style={{ width: "100%" }}
                              reservations={freeTablesReservations}
                              past_estimated_time={true}
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: "15px",
                          overflowBehaviour: "contain",
                        }}
                      >
                        {allReservations &&
                          allReservations
                            .filter((reservation) => {
                              const notifications =
                                typeof reservation.chat_notification_restaurant ===
                                "string"
                                  ? reservation.chat_notification_restaurant.split(
                                      ",",
                                    )
                                  : [];

                              return (
                                (reservation.chat_notification_restaurant_all ||
                                  (reservation.chat_notification_restaurant &&
                                    notifications.includes(userInfo.name))) &&
                                reservation.chat_has_messages
                              );
                            })
                            .map((reservation) => (
                              <CollapsibleChat
                                key={reservation.id}
                                reservation={reservation}
                                isClient={false}
                                style={{ width: "100%" }}
                                isNewMessage={true}
                                onToggle={handleChatToggle}
                              />
                            ))}

                        {reservationsWithChatMessages &&
                          reservationsWithChatMessages
                            .filter((reservation) => {
                              const notifications =
                                typeof reservation.chat_notification_restaurant ===
                                "string"
                                  ? reservation.chat_notification_restaurant.split(
                                      ",",
                                    )
                                  : [];

                              return (
                                !reservation.chat_notification_restaurant_all &&
                                (!reservation.chat_notification_restaurant ||
                                  !notifications.includes(userInfo.name)) &&
                                reservation.chat_has_messages
                              );
                            })
                            .map((reservation) => (
                              <CollapsibleChat
                                key={reservation.id}
                                reservation={reservation}
                                isClient={false}
                                style={{ width: "100%" }}
                                isNewMessage={false}
                                onToggle={handleChatToggle}
                              />
                            ))}
                      </div>
                    )}
                  </div>
                </div>
              </InsideContainer>
            </InsideContainerBox>
          </Col>
        </Row>
      </BarContainer>
    </>
  );
}

export default NotificationBell;
